/**
 * OnboardingTour v2 — 3-step "Getting Started" intro.
 *
 * Replaces the old 14-step linear tour (ui-review-phase2 §2).
 * Fires once on first login; stored under 'ch-tour-v2-done'.
 * Re-launchable via Help → "Restart intro tour".
 *
 * Steps:
 *   1. Ask anything     — highlights the query textarea
 *   2. Your query modes — highlights the mode pill row
 *   3. The sidebar      — highlights the left sidebar
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/* ── Types ─────────────────────────────────────────────────── */

interface TourStep {
  target: string;           // data-tour="<target>" selector
  title: string;
  description: string;
  /** Optional placement; defaults to 'auto' */
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
}

interface OnboardingTourProps {
  /** Force-show even if already completed (for the Help → Restart trigger) */
  forceShow?: boolean;
  onComplete?: () => void;
}

/* ── Steps ─────────────────────────────────────────────────── */

const STEPS: TourStep[] = [
  {
    target: 'query-input',
    title: 'Ask anything',
    description:
      'Type any compliance question — standards clauses, NCC requirements, WaterMark specs. Get a cited answer in seconds.',
    placement: 'top',
  },
  {
    target: 'mode-pills',
    title: 'Your query modes',
    description:
      'Switch modes for different output formats. Checklist for audit checklists, Compare for side-by-side jurisdiction comparison.',
    placement: 'top',
  },
  {
    target: 'sidebar-nav',
    title: 'Everything else is in the sidebar',
    description:
      'Reports, copy builder, manual review, corpus browser, your research notes — all in the sidebar. Come back to it anytime.',
    placement: 'right',
  },
];

/** v2 localStorage key */
const LS_KEY_V2 = 'ch-tour-v2-done';
/** Legacy key — checked for backwards compatibility but not set */
const LS_KEY_LEGACY = 'standards-onboarding-complete';

/* ── Utilities ─────────────────────────────────────────────── */

function getTargetEl(target: string): HTMLElement | null {
  return document.querySelector(`[data-tour="${target}"]`);
}

type PlacementResolved = 'top' | 'bottom' | 'left' | 'right';

function resolvePlacement(
  rect: DOMRect,
  placement: TourStep['placement'] = 'auto',
): PlacementResolved {
  if (placement !== 'auto') return placement as PlacementResolved;
  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;
  return spaceBelow > spaceAbove ? 'bottom' : 'top';
}

interface TooltipPos {
  top: number;
  left: number;
  arrowSide: 'top' | 'bottom' | 'left' | 'right';
  arrowOffset: number;
}

function computeTooltipPos(
  rect: DOMRect,
  placement: PlacementResolved,
  tooltipW: number,
  tooltipH: number,
): TooltipPos {
  const GAP = 12;
  const PAD = 12;
  const ARROW_MARGIN = 16;

  let top = 0;
  let left = 0;
  let arrowSide: TooltipPos['arrowSide'] = 'top';

  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;

  switch (placement) {
    case 'bottom':
      top = rect.bottom + GAP;
      left = cx - tooltipW / 2;
      arrowSide = 'top';
      break;
    case 'top':
      top = rect.top - tooltipH - GAP;
      left = cx - tooltipW / 2;
      arrowSide = 'bottom';
      break;
    case 'left':
      top = cy - tooltipH / 2;
      left = rect.left - tooltipW - GAP;
      arrowSide = 'right';
      break;
    case 'right':
      top = cy - tooltipH / 2;
      left = rect.right + GAP;
      arrowSide = 'left';
      break;
  }

  const clampedLeft = Math.max(PAD, Math.min(left, window.innerWidth - tooltipW - PAD));
  const clampedTop = Math.max(PAD, Math.min(top, window.innerHeight - tooltipH - PAD));

  // Adjust arrow so it still points at the target even if tooltip was clamped
  let arrowOffset = 0;
  if (placement === 'top' || placement === 'bottom') {
    arrowOffset = cx - (clampedLeft + tooltipW / 2);
    arrowOffset = Math.max(-(tooltipW / 2 - ARROW_MARGIN), Math.min(tooltipW / 2 - ARROW_MARGIN, arrowOffset));
  } else {
    arrowOffset = cy - (clampedTop + tooltipH / 2);
    arrowOffset = Math.max(-(tooltipH / 2 - ARROW_MARGIN), Math.min(tooltipH / 2 - ARROW_MARGIN, arrowOffset));
  }

  return { top: clampedTop, left: clampedLeft, arrowSide, arrowOffset };
}

/* ── Component ─────────────────────────────────────────────── */

export default function OnboardingTour({ forceShow, onComplete }: OnboardingTourProps) {
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<TooltipPos | null>(null);
  const [spotlightRect, setSpotlightRect] = useState<DOMRect | null>(null);

  /* ── Mount: decide whether to show ── */
  useEffect(() => {
    if (forceShow) {
      setActive(true);
      setStep(0);
      return;
    }
    try {
      // v2 key — never shown yet
      if (localStorage.getItem(LS_KEY_V2) !== 'true') {
        // Also check legacy key: if user already did the old tour, still show v2
        // (v2 is short enough that it's worth showing to existing users too)
        setActive(true);
      }
    } catch { /* ignore */ }
  }, [forceShow]);

  /* ── Auto-skip steps whose target isn't in the DOM ── */
  const skipGuardRef = useRef(0);
  useEffect(() => {
    if (!active) return;
    const current = STEPS[step];
    if (!current) return;
    const el = getTargetEl(current.target);
    if (!el) {
      if (skipGuardRef.current < STEPS.length) {
        skipGuardRef.current++;
        if (step < STEPS.length - 1) {
          setStep((s) => s + 1);
        } else {
          finish();
        }
      }
    } else {
      skipGuardRef.current = 0;
    }
  });

  /* ── Reposition ── */
  const reposition = useCallback(() => {
    if (!active) return;
    const current = STEPS[step];
    if (!current) return;
    const el = getTargetEl(current.target);
    if (el) {
      const rect = el.getBoundingClientRect();
      setSpotlightRect(rect);
      const tooltip = tooltipRef.current;
      const w = tooltip?.offsetWidth ?? 340;
      const h = tooltip?.offsetHeight ?? 160;
      const placement = resolvePlacement(rect, current.placement);
      setPos(computeTooltipPos(rect, placement, w, h));
    } else {
      setSpotlightRect(null);
      setPos(null);
    }
  }, [active, step]);

  useLayoutEffect(() => { reposition(); }, [reposition]);

  useEffect(() => {
    if (!active) return;
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [active, reposition]);

  /* ── Keyboard ── */
  useEffect(() => {
    if (!active) return;
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  });

  /* ── Navigation ── */
  const finish = useCallback(() => {
    setActive(false);
    try {
      localStorage.setItem(LS_KEY_V2, 'true');
      // Also mark legacy key so old checks don't re-trigger
      localStorage.setItem(LS_KEY_LEGACY, 'true');
    } catch { /* ignore */ }
    onComplete?.();
  }, [onComplete]);

  const next = useCallback(() => {
    if (step < STEPS.length - 1) setStep((s) => s + 1);
    else finish();
  }, [step, finish]);

  const prev = useCallback(() => {
    if (step > 0) setStep((s) => s - 1);
  }, [step]);

  if (!active) return null;

  const current = STEPS[step];
  const isFirst = step === 0;
  const isLast = step === STEPS.length - 1;

  /* ── Spotlight SVG path ── */
  const PAD = 8;
  const R = 8;
  let spotlightPath = '';
  if (spotlightRect) {
    const x = spotlightRect.left - PAD;
    const y = spotlightRect.top - PAD;
    const w = spotlightRect.width + PAD * 2;
    const h = spotlightRect.height + PAD * 2;
    spotlightPath = [
      `M0,0 H${window.innerWidth} V${window.innerHeight} H0Z`,
      // Rounded rect cut-out (counter-clockwise)
      `M${x + R},${y}`,
      `H${x + w - R} Q${x + w},${y} ${x + w},${y + R}`,
      `V${y + h - R} Q${x + w},${y + h} ${x + w - R},${y + h}`,
      `H${x + R} Q${x},${y + h} ${x},${y + h - R}`,
      `V${y + R} Q${x},${y} ${x + R},${y}Z`,
    ].join(' ');
  } else {
    spotlightPath = `M0,0H${window.innerWidth}V${window.innerHeight}H0Z`;
  }

  return (
    <div className="onboarding-overlay" aria-modal="true" role="dialog" aria-label="Getting started tour">
      {/* Dark overlay with spotlight cut-out */}
      <svg
        className="onboarding-backdrop"
        width={window.innerWidth}
        height={window.innerHeight}
        viewBox={`0 0 ${window.innerWidth} ${window.innerHeight}`}
        onClick={finish}
      >
        <path d={spotlightPath} fill="rgba(16,16,18,0.85)" fillRule="evenodd" />
      </svg>

      {/* Spotlight ring */}
      {spotlightRect && (
        <div
          className="onboarding-spotlight-ring"
          style={{
            top: spotlightRect.top - PAD,
            left: spotlightRect.left - PAD,
            width: spotlightRect.width + PAD * 2,
            height: spotlightRect.height + PAD * 2,
            borderRadius: R,
          }}
        />
      )}

      {/* Tooltip card */}
      <div
        ref={tooltipRef}
        className="onboarding-tooltip"
        style={
          pos
            ? { top: pos.top, left: pos.left }
            : { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
        }
      >
        {/* Arrow */}
        {pos && (
          <div
            className={`onboarding-arrow onboarding-arrow--${pos.arrowSide}`}
            style={
              pos.arrowSide === 'top' || pos.arrowSide === 'bottom'
                ? { marginLeft: `calc(-6px + ${pos.arrowOffset}px)` }
                : { marginTop: `calc(-6px + ${pos.arrowOffset}px)` }
            }
          />
        )}

        {/* Step counter */}
        <div className="onboarding-step-counter">
          Step {step + 1} of {STEPS.length}
        </div>

        <h3 className="onboarding-title">{current.title}</h3>
        <p className="onboarding-desc">{current.description}</p>

        {/* Progress dots */}
        <div className="onboarding-dots" aria-hidden="true">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={`onboarding-dot${i === step ? ' onboarding-dot--active' : ''}${i < step ? ' onboarding-dot--done' : ''}`}
            />
          ))}
        </div>

        {/* Controls */}
        <div className="onboarding-buttons">
          <button
            className="onboarding-btn onboarding-btn--skip"
            onClick={finish}
          >
            Skip tour
          </button>
          <div className="onboarding-buttons-right">
            {!isFirst && (
              <button className="onboarding-btn onboarding-btn--back" onClick={prev}>
                Back
              </button>
            )}
            <button className="onboarding-btn onboarding-btn--next" onClick={next}>
              {isLast ? 'Got it' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── HelpIcon — kept for backwards compat with any imports ── */
export function HelpIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
